import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getStandardNavigationCatalogue, type StandardNavigationItem } from '@cdf/navigation-catalogue';

import { useAuth } from '@/components/auth/auth-provider';
import { useSession } from '@/components/auth/user-provider';
import { useTenantEntity } from '@/components/access/tenant-entity-provider';

type Access = ReturnType<typeof useTenantEntity>;
// Shell utility pages are not all sidebar catalogue entries.
const catalogue: readonly Pick<StandardNavigationItem, 'to' | 'moduleKey' | 'permissions'>[] = [
  ...getStandardNavigationCatalogue(),
  { to: '/work' },
  { to: '/channel-connect' },
  { to: '/knowledge' },
  { to: '/pages' },
  { to: '/settings', permissions: ['tenant.members.manage'] }
];
const safeQueryKeys = new Set(['view', 'tab', 'section', 'page', 'limit', 'offset', 'sort', 'order']);
const storagePrefix = 'cdf-last-page:v1:';

/** Store navigation preferences only, never arbitrary query payloads or auth callbacks. */
export function resumablePage(value: string, access: Access): string | null {
  if (!value.startsWith('/') || value.startsWith('//') || value.length > 2048
    || /[\\\u0000-\u0020]/.test(value)) return null;
  try {
    const url = new URL(value, 'https://app.invalid');
    if (url.origin !== 'https://app.invalid' || /%2f|%5c|%00/i.test(url.pathname)) return null;
    const path = url.pathname.replace(/\/+$/, '') || '/';
    const matches = catalogue.filter(item => {
      const base = item.to.split('?')[0]!;
      return !base.includes(':') && (path === base || path.startsWith(base + '/'));
    }).sort((a, b) => b.to.split('?')[0]!.length - a.to.split('?')[0]!.length);
    const item = matches.find(candidate => candidate.moduleKey) ?? matches[0];
    // These are presentation checks only. Native page and API authorization still apply.
    if (!item || (item.moduleKey && !access.hasModule(item.moduleKey))) return null;
    const hasPermission = (key: string) => key.startsWith('platform.')
      ? access.hasPlatformPermission(key) : access.hasPermission(key);
    const permissions = item.permissions ?? [];
    const administrative = path.startsWith('/admin/') || path.startsWith('/settings') || path.startsWith('/manage/');
    if (permissions.length && !(administrative ? permissions.some(hasPermission) : permissions.every(hasPermission))) return null;
    if (path === '/admin/platform' && !access.platformPermissions.length) return null;
    const search = new URLSearchParams();
    for (const [key, entry] of url.searchParams) {
      if (safeQueryKeys.has(key) && /^[\w.-]{1,80}$/.test(entry)) search.set(key, entry);
    }
    return path + (search.size ? '?' + search.toString() : '');
  } catch {
    return null;
  }
}

export function lastPageStorageKey(identity: string, tenantId: string, entityId: string): string {
  return storagePrefix + JSON.stringify([identity, tenantId, entityId]);
}

export function readLastPage(key: string | null, access: Access): string | null {
  if (!key) return null;
  try {
    const value = localStorage.getItem(key);
    return value ? resumablePage(value, access) : null;
  } catch {
    return null;
  }
}

export function useLastPageScope() {
  const auth = useAuth();
  const { session } = useSession();
  const access = useTenantEntity();
  const identity = auth.enabled ? auth.account?.stableId : 'local-development';
  const key = session.status === 'signedIn' && session.user.role !== 'customer'
    && access.status === 'ready' && identity && access.selected
    ? lastPageStorageKey(identity, access.selected.tenantId, access.selected.entityId) : null;
  return { key, access };
}

/** Mounted in the signed-in shell; the root landing route owns restoration. */
export function RememberLastPage() {
  const { key, access } = useLastPageScope();
  const { pathname, search } = useLocation();
  const previous = useRef<{ key: string; location: string } | null>(null);
  const location = pathname + search;
  useEffect(() => {
    if (!key) return;
    const prior = previous.current;
    // A scope switch is not a visit to the same record in another entity/person.
    if (prior && prior.key !== key && prior.location === location) return;
    previous.current = { key, location };
    const destination = resumablePage(location, access);
    if (!destination) return;
    try {
      localStorage.setItem(key, destination);
    } catch {
      // Navigation still works when browser storage is blocked or full.
    }
  }, [key, location, access]);
  return null;
}
